<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9999] overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
      @click.self="close"
    >
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[9998]"
        aria-hidden="true"
        @click="close"
      ></div>

      <!-- Center modal -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

      <div
        class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full relative z-[10000]"
      >
        <form @submit.prevent="handleSubmit">
          <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 sm:mx-0 sm:h-10 sm:w-10">
                <svg class="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                  {{ editingAgent ? 'Edit AI Agent' : 'Create New AI Agent' }}
                </h3>
                <div class="mt-4 space-y-4">
                  <!-- Agent Name -->
                  <div>
                    <label for="agent-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Agent Name <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="agent-name"
                      v-model="form.name"
                      type="text"
                      required
                      class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="e.g., Customer Support Agent"
                    />
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Primary name for the agent
                    </p>
                  </div>

                  <!-- Multiple Agent Names -->
                  <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Additional Names (Optional, max 10)
                    </label>
                    <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
                      Add up to 10 names. A random name will be selected each time a user visits.
                    </p>
                    <div class="space-y-2">
                      <div
                        v-for="(_name, index) in (form.agentNames || [])"
                        :key="index"
                        class="flex gap-2"
                      >
                        <input
                          v-model="form.agentNames![index]"
                          type="text"
                          class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          :placeholder="`Name ${index + 1}`"
                        />
                        <button
                          type="button"
                          @click="removeAgentName(index)"
                          class="px-3 py-2 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Remove name"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                          </svg>
                        </button>
                      </div>
                      <button
                        v-if="!form.agentNames || form.agentNames.length < 10"
                        type="button"
                        @click="addAgentName"
                        class="w-full px-4 py-2.5 border-2 border-dashed border-blue-400 dark:border-blue-500 rounded-md text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-sm font-medium transition-colors flex items-center justify-center"
                      >
                        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                        </svg>
                        Add Name ({{ form.agentNames?.length || 0 }}/10)
                      </button>
                      <p v-if="form.agentNames && form.agentNames.length === 0" class="text-xs text-gray-400 dark:text-gray-500 text-center py-2">
                        No additional names added yet
                      </p>
                    </div>
                  </div>

                  <!-- Description -->
                  <div>
                    <label for="agent-description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description
                    </label>
                    <textarea
                      id="agent-description"
                      v-model="form.description"
                      rows="2"
                      class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Brief description of what this agent does..."
                    ></textarea>
                  </div>

                  <!-- AI Model Selection -->
                  <div>
                    <label for="ai-model" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      AI Model <span class="text-red-500">*</span>
                    </label>
                    <select
                      id="ai-model"
                      v-model="form.modelId"
                      required
                      class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option :value="0">-- Select AI Model --</option>
                      <option v-for="model in availableModels" :key="model.id" :value="model.id">
                        {{ model.name }} ({{ model.provider }})
                      </option>
                    </select>
                    <p v-if="selectedModelDescription" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {{ selectedModelDescription }}
                    </p>
                    <p v-else class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Choose which AI model to use for this agent
                    </p>
                  </div>
                  <div v-if="availableModels.length === 0 && !loadingModels" class="rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-3">
                    <p class="text-sm text-yellow-800 dark:text-yellow-200">
                      No AI models available. Please contact support.
                    </p>
                  </div>

                  <!-- Color Theme Selection -->
                  <div>
                    <label for="color-theme" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Color Theme
                    </label>
                    <div class="mt-2 grid grid-cols-4 gap-2">
                      <button
                        v-for="theme in colorThemes"
                        :key="theme.value"
                        type="button"
                        @click="form.colorTheme = theme.value"
                        :class="[
                          'h-12 rounded-lg border-2 transition-all',
                          form.colorTheme === theme.value 
                            ? 'border-gray-900 dark:border-white scale-105' 
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400',
                          theme.bgClass
                        ]"
                        :title="theme.label"
                      >
                        <span class="sr-only">{{ theme.label }}</span>
                      </button>
                    </div>
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Choose a color theme for your AI agent's chat widget
                    </p>
                  </div>

                  <!-- System Prompt -->
                  <div>
                    <label for="system-prompt" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      System Prompt <span class="text-red-500">*</span>
                    </label>
                    <textarea
                      id="system-prompt"
                      v-model="form.systemPrompt"
                      rows="4"
                      required
                      class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono text-sm"
                      placeholder="You are a helpful customer support agent..."
                    ></textarea>
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Define how the AI agent should behave and respond to users
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="error" class="px-4 sm:px-6 pb-2">
            <div class="rounded-md bg-red-50 dark:bg-red-900/20 p-3">
              <p class="text-sm text-red-800 dark:text-red-200">{{ error }}</p>
            </div>
          </div>

          <!-- Actions -->
          <div class="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="submit"
              :disabled="loading || !form.modelId || availableModels.length === 0"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="loading" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ editingAgent ? 'Updating...' : 'Creating...' }}
              </span>
              <span v-else>{{ editingAgent ? 'Update Agent' : 'Create Agent' }}</span>
            </button>
            <button
              type="button"
              @click="close"
              :disabled="loading"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { agentsService, type CreateAgentRequest, type UpdateAgentRequest, type Agent } from '../services/agents'
import { modelsService, type AIModel } from '../services/models'

interface Props {
  isOpen: boolean
  editingAgent?: Agent | null
}

interface Emits {
  (e: 'close'): void
  (e: 'created'): void
  (e: 'updated'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const form = ref<CreateAgentRequest>({
  name: '',
  description: '',
  systemPrompt: '',
  modelId: 0,
  colorTheme: 'purple',
  agentNames: []
})

const colorThemes = [
  { value: 'purple', label: 'Purple', bgClass: 'bg-gradient-to-br from-purple-500 to-purple-700' },
  { value: 'blue', label: 'Blue', bgClass: 'bg-gradient-to-br from-blue-500 to-blue-700' },
  { value: 'light-blue', label: 'Light Blue', bgClass: 'bg-gradient-to-br from-blue-400 to-cyan-500' },
  { value: 'green', label: 'Green', bgClass: 'bg-gradient-to-br from-green-500 to-green-700' },
  { value: 'orange', label: 'Orange', bgClass: 'bg-gradient-to-br from-orange-500 to-orange-700' },
  { value: 'yellow', label: 'Yellow', bgClass: 'bg-gradient-to-br from-yellow-400 to-yellow-600' },
  { value: 'red', label: 'Red', bgClass: 'bg-gradient-to-br from-red-500 to-red-700' },
  { value: 'pink', label: 'Pink', bgClass: 'bg-gradient-to-br from-pink-500 to-pink-700' }
]

const loading = ref(false)
const error = ref<string | null>(null)
const availableModels = ref<AIModel[]>([])
const loadingModels = ref(false)

const selectedModelDescription = computed(() => {
  const selected = availableModels.value.find(m => m.id === form.value.modelId)
  return selected?.description || null
})

// Load available models when modal opens
watch(() => props.isOpen, async (isOpen) => {
  console.log('CreateAgentModal: isOpen prop changed to:', isOpen)
  if (isOpen) {
    loadingModels.value = true
    try {
      availableModels.value = await modelsService.getAvailableModels()
      if (availableModels.value.length === 0) {
        error.value = 'No AI models available. Please contact support.'
      }
      
      // If editing, populate form with agent data
      if (props.editingAgent) {
        // Parse agent_names from JSON if it exists
        let agentNames: string[] = []
        if (props.editingAgent.agent_names) {
          if (typeof props.editingAgent.agent_names === 'string') {
            try {
              agentNames = JSON.parse(props.editingAgent.agent_names)
            } catch (e) {
              agentNames = []
            }
          } else if (Array.isArray(props.editingAgent.agent_names)) {
            agentNames = props.editingAgent.agent_names
          }
        }
        
        form.value = {
          name: props.editingAgent.name,
          description: props.editingAgent.description || '',
          systemPrompt: props.editingAgent.system_prompt,
          modelId: (props.editingAgent as any).model_id || 0,
          colorTheme: props.editingAgent.color_theme || 'purple',
          agentNames: agentNames.length > 0 ? agentNames : []
        }
      }
    } catch (err) {
      console.error('Failed to load available models:', err)
      error.value = 'Failed to load available models'
    } finally {
      loadingModels.value = false
    }
  } else {
    resetForm()
  }
}, { immediate: true })

const addAgentName = () => {
  if (!form.value.agentNames) {
    form.value.agentNames = []
  }
  if (form.value.agentNames.length < 10) {
    form.value.agentNames.push('')
  }
}

const removeAgentName = (index: number) => {
  if (form.value.agentNames) {
    form.value.agentNames.splice(index, 1)
  }
}

const resetForm = () => {
  form.value = {
    name: '',
    description: '',
    systemPrompt: '',
    modelId: 0,
    colorTheme: 'purple',
    agentNames: []
  }
  error.value = null
}

const close = () => {
  if (!loading.value) {
    resetForm()
    emit('close')
  }
}

const handleSubmit = async () => {
  if (loading.value) return

  if (!form.value.modelId || form.value.modelId === 0) {
    error.value = 'Please select an AI model'
    return
  }

  console.log('Form submitted:', form.value)
  error.value = null
  loading.value = true

  try {
    if (props.editingAgent) {
      // Update existing agent
      console.log('Calling agentsService.updateAgent...')
      // Filter out empty names
      const validNames = form.value.agentNames?.filter(name => name.trim().length > 0) || []
      const updateRequest: UpdateAgentRequest = {
        name: form.value.name,
        description: form.value.description,
        systemPrompt: form.value.systemPrompt,
        modelId: form.value.modelId,
        colorTheme: form.value.colorTheme,
        agentNames: validNames.length > 0 ? validNames : undefined
      }
      const result = await agentsService.updateAgent(props.editingAgent.id, updateRequest)
      console.log('Agent updated successfully:', result)
      resetForm()
      emit('updated')
      emit('close')
    } else {
      // Create new agent
      console.log('Calling agentsService.createAgent...')
      // Filter out empty names
      const validNames = form.value.agentNames?.filter(name => name.trim().length > 0) || []
      const createRequest: CreateAgentRequest = {
        ...form.value,
        agentNames: validNames.length > 0 ? validNames : undefined
      }
      const result = await agentsService.createAgent(createRequest)
      console.log('Agent created successfully:', result)
      resetForm()
      emit('created')
      emit('close')
    }
  } catch (err) {
    console.error('Error saving agent:', err)
    error.value = err instanceof Error ? err.message : (props.editingAgent ? 'Failed to update agent' : 'Failed to create agent')
  } finally {
    loading.value = false
  }
}

</script>
